import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MfaSetup } from '@up2d8/contracts';
import { saveBlob } from '../../shared/download';
import { useSession, type SecondFactor } from '../../shared/session';
import { useProblemText } from '../../shared/use-problem-text';
import { Btn, Checkbox, Field, Form, Input, Note, Stack } from '../admin-kit';

/**
 * De tweede stap bij het aanmelden (FBK-12, ADR-0095), voor het beheer, de app en Master.
 *
 * Twee vormen: de code uit de authenticator-app ingeven (of een herstelcode), en, als 2FA
 * verplicht is maar nog niet ingesteld, de app koppelen met een QR-code. Pas na de eerste juiste
 * code bestaat er een sessie; daarna zie je één keer de herstelcodes.
 */

/** De code uit de app, of een herstelcode. */
export function MfaCodeStep({
  onSubmit,
  busy,
  failed,
}: {
  onSubmit: (second: SecondFactor) => void;
  busy: boolean;
  failed?: boolean;
}) {
  const { t } = useTranslation();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');

  return (
    <Form
      onSubmit={() =>
        onSubmit(recovery ? { recoveryCode: code.trim() } : { totpCode: code.replace(/\s/g, '') })
      }
    >
      <Note tone={failed ? 'danger' : 'info'} role={failed ? 'alert' : 'status'}>
        {failed ? t('mfa.codeFailed') : recovery ? t('mfa.recoveryNeeded') : t('mfa.codeNeeded')}
      </Note>
      <Field label={recovery ? t('mfa.recoveryLabel') : t('mfa.codeLabel')}>
        {(props) => (
          <Input
            {...props}
            name={recovery ? 'recoveryCode' : 'totpCode'}
            inputMode={recovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            data-testid="mfa-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
          />
        )}
      </Field>
      <Btn type="submit" size="lg" block disabled={busy || code.trim().length < 6}>
        {t('mfa.submit')}
      </Btn>
      <button
        type="button"
        className="a-link"
        onClick={() => {
          setRecovery(!recovery);
          setCode('');
        }}
      >
        {recovery ? t('mfa.useApp') : t('mfa.useRecovery')}
      </button>
    </Form>
  );
}

/** Een QR-code als afbeelding: de SVG van de server, zonder `innerHTML`. */
export function QrImage({ svg, label }: { svg: string; label: string }) {
  return (
    <img
      src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
      alt={label}
      width={200}
      height={200}
      style={{ display: 'block', margin: '0 auto', background: 'white', borderRadius: 8 }}
      data-testid="mfa-qr"
    />
  );
}

/** Het geheim in groepjes van vier, om over te tikken. */
export function groupedSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/** De herstelcodes, één keer getoond, met downloaden en een bevestiging dat ze bewaard zijn. */
export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  return (
    <Stack>
      <Note tone="attention">{t('mfa.recoveryIntro')}</Note>
      <ol
        className="a-mono"
        data-testid="mfa-recovery-codes"
        style={{ columns: 2, margin: 0, paddingLeft: 24, lineHeight: 1.9 }}
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ol>
      <Btn
        variant="sec"
        onClick={() =>
          void saveBlob(
            new Blob([`${t('mfa.recoveryFileTitle')}\n\n${codes.join('\n')}\n`], {
              type: 'text/plain',
            }),
            'herstelcodes.txt',
          )
        }
      >
        {t('mfa.download')}
      </Btn>
      <Checkbox checked={saved} onChange={setSaved} label={t('mfa.saved')} testId="mfa-saved" />
      <Btn size="lg" block disabled={!saved} testId="mfa-continue" onClick={onDone}>
        {t('mfa.continue')}
      </Btn>
    </Stack>
  );
}

/** De afgedwongen inschrijving: QR-code, eerste code, dan de herstelcodes. */
export function MfaEnrollStep({
  enrollToken,
  onDone,
  onExpired,
}: {
  enrollToken: string;
  onDone: () => void;
  onExpired: () => void;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const text = useProblemText();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);

  const setup = useQuery({
    queryKey: ['mfa-enroll', enrollToken],
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<MfaSetup> => {
      const { data, error } = await session.api.POST('/api/v1/auth/mfa/enroll/start', {
        body: { enrollToken } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as MfaSetup;
    },
  });

  const confirm = useMutation({
    mutationFn: () => session.completeMfaEnrollment(enrollToken, code.replace(/\s/g, '')),
    onSuccess: setCodes,
  });

  if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />;

  if (setup.isError) {
    return (
      <Stack>
        <Note tone="danger" role="alert">
          {text(setup.error, t('mfa.expired'))}
        </Note>
        <Btn variant="sec" onClick={onExpired}>
          {t('mfa.restart')}
        </Btn>
      </Stack>
    );
  }

  return (
    <Form onSubmit={() => confirm.mutate()}>
      <Note tone="info">{t('mfa.enrollIntro')}</Note>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
        <li>{t('mfa.enrollStep1')}</li>
        <li>{t('mfa.enrollStep2')}</li>
        <li>{t('mfa.enrollStep3')}</li>
      </ol>
      {setup.data ? (
        <>
          <QrImage svg={setup.data.qrSvg} label={t('mfa.qrLabel')} />
          <div className="a-hint" style={{ textAlign: 'center' }}>
            {t('mfa.manualKey')}{' '}
            <span className="a-mono" data-testid="mfa-secret">
              {groupedSecret(setup.data.secret)}
            </span>
          </div>
        </>
      ) : null}
      {confirm.isError ? (
        <Note tone="danger" role="alert">
          {text(confirm.error, t('mfa.codeFailed'))}
        </Note>
      ) : null}
      <Field label={t('mfa.codeLabel')}>
        {(props) => (
          <Input
            {...props}
            inputMode="numeric"
            autoComplete="one-time-code"
            data-testid="mfa-enroll-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
          />
        )}
      </Field>
      <Btn
        type="submit"
        size="lg"
        block
        testId="mfa-enroll-confirm"
        disabled={!setup.data || confirm.isPending || code.replace(/\s/g, '').length !== 6}
      >
        {t('mfa.enrollConfirm')}
      </Btn>
    </Form>
  );
}
